import React, { createContext, useState, useContext, useEffect } from 'react'
import { authService } from '../services/api'

// Create Auth Context
const AuthContext = createContext()

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      if (!token) {
        setLoading(false)
        return
      }

      // Verify token and get user data
      const response = await authService.getProfile()
      setUser(response.data.data)
      setError(null)
    } catch (err) {
      // Token is invalid or expired
      localStorage.removeItem('token')
      setUser(null)
      setError('Session expired. Please login again.')
    } finally {
      setLoading(false)
    }
  }

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await authService.login(credentials)
      const { token, user: userData } = response.data.data

      // Store token in localStorage
      localStorage.setItem('token', token)
      
      // Set user in state
      setUser(userData)
      
      return { success: true, user: userData }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await authService.register(userData)
      const { token, user: newUser } = response.data.data

      // Store token in localStorage
      localStorage.setItem('token', token)
      
      // Set user in state
      setUser(newUser)
      
      return { success: true, user: newUser }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed. Please try again.'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token')
    
    // Clear user state
    setUser(null)
    setError(null)
    
    // Optional: Redirect to login page
    window.location.href = '/login'
  }

  // Update user profile
  const updateUser = (updatedUser) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUser
    }))
  }

  // Clear error
  const clearError = () => {
    setError(null)
  }

  // Check if user is authenticated
  const isAuthenticated = !!user

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    clearError,
    isAuthenticated,
    checkAuthStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext